import React, { useState } from 'react';
import {
  Banner,
  Button,
  Gutter,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Flashy,
  Checkbox,
  Textarea,
  Form,
  ApiError,
  FormGroup,
  Layout,
  Radio,
  LayoutItem,
  CondensedLarge,
  Grid,
  GridRow,
  GridColumn,
  Icon,
  Accolade,
  Well,
  SectionHeader,
} from '@axiom/ui';
import type { Candidate, CandidateWorkFeedItem } from '@axiom/validation';
import { ExpressInterestSchema } from '@axiom/validation';

import { CandidateOpportunitiesApi } from '../../api/candidate-opportunities-api';
import { WorkFeedUtil } from '../../utils/WorkFeedUtil';
import { WorkFeedEOIJobDescription } from '../WorkFeedEOIJobDescription/WorkFeedEOIJobDescription';
import { DISCOUNT_PERCENTAGES } from '../../utils/work-feed-interest-util';

export type WorkFeedConfirmInterestSelfServiceModal = {
  candidate: Candidate;
  name?: string;
  onAfterSubmit: () => Promise<void>;
  onClose: () => void;
  workFeedItem: CandidateWorkFeedItem;
  submitButtonText?: string;
};

export const WorkFeedConfirmInterestSelfServiceModal = ({
  candidate,
  name = 'CONFIRM_INTEREST_SELF_SERVICE_MODAL',
  onAfterSubmit,
  onClose,
  workFeedItem,
  submitButtonText = 'Submit',
}: WorkFeedConfirmInterestSelfServiceModal) => {
  const [
    showOpenToLowerEscCompensationPercentage,
    setShowOpenToLowerEscCompensationPercentage,
  ] = useState(false);

  return (
    <Form
      name={`${name}_FORM`}
      schema={ExpressInterestSchema}
      initialValues={{
        opportunityId: workFeedItem.opportunity.id,
        roleFitDescription: candidate.candidateSummary || '',
      }}
      onSubmit={async (changedData, actions, values) => {
        try {
          await CandidateOpportunitiesApi.createWorkFeedInterested({
            candidateId: candidate.id,
            body: {
              ...values,
              roleFitDescription:
                changedData.roleFitDescription ??
                candidate.candidateSummary ??
                '',
              opportunityId: workFeedItem.opportunity.id,
            },
          });

          await onAfterSubmit();
        } catch (e) {
          if (e instanceof ApiError && e.getStatusCode() === 424) {
            WorkFeedUtil.addErrorToast(candidate.ownerUser?.email);
            return;
          }
          throw e;
        }
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name={name} size="large">
            <ModalHeader
              name="PENDO_CONFIRM_INTEREST_SELF_SERVICE_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Submit profile to{' '}
              {workFeedItem.opportunity.accountName ?? 'Client'}
            </ModalHeader>
            <ModalSection>
              <Grid>
                <GridRow>
                  <GridColumn smallScreenWidth={12} largeScreenWidth={8}>
                    <Gutter bottom="24px">
                      <Accolade
                        color="purple"
                        label="Self-service"
                        name="SELF_SERVICE"
                      >
                        <Icon name="star-filled" />
                      </Accolade>
                    </Gutter>
                    <Gutter bottom="24px">
                      <Well
                        background="blue"
                        name="SUBMISSION_NOTE_DESCRIPTION"
                      >
                        <SectionHeader>
                          Include a submission note for{' '}
                          {workFeedItem.opportunity.accountName}
                        </SectionHeader>
                        <Gutter bottom="8px" />
                        Briefly describe why you are interested in this work and
                        what makes you uniquely qualified. This submission note
                        will go directly to{' '}
                        {workFeedItem.opportunity.accountName}.
                      </Well>
                    </Gutter>
                    <Gutter bottom="24px">
                      <Textarea
                        label="Submission note"
                        name="roleFitDescription"
                        enableSuggestionsFor="portal-WorkFeedDirectExpressionModal-roleFitDescription"
                      />
                    </Gutter>
                    <Gutter bottom="24px">
                      <Checkbox
                        name="openToLowerEsc"
                        onChange={() => {
                          setShowOpenToLowerEscCompensationPercentage(
                            !showOpenToLowerEscCompensationPercentage
                          );
                        }}
                        displayValue="I would be willing to discuss accepting moderately lower compensation for this specific opportunity if the client's budget is restrictive."
                      />
                    </Gutter>
                    {showOpenToLowerEscCompensationPercentage ? (
                      <Gutter bottom="24px">
                        <Banner>
                          <LayoutItem bottomGutter="8px">
                            <CondensedLarge>
                              How much of a reduction in compensation would you
                              be willing to accept for this opportunity? If
                              Axiom moves forward with your candidacy, we will
                              call you to finalize your rate.{' '}
                              <Flashy color="textAlert">*</Flashy>
                            </CondensedLarge>
                          </LayoutItem>
                          <FormGroup name="openToLowerEscCompensationPercentage">
                            <Layout direction="vertical" verticalGutter="8px">
                              {DISCOUNT_PERCENTAGES.map(item => (
                                <Radio
                                  key={item}
                                  name="openToLowerEscCompensationPercentage"
                                  option={item}
                                  displayValue={`Up to ${item}%`}
                                />
                              ))}
                            </Layout>
                          </FormGroup>
                        </Banner>
                      </Gutter>
                    ) : null}
                    <Checkbox
                      name="hasNoConflicts"
                      displayValue="To my knowledge this opportunity does not present any actual or potential conflicts of interest that would prevent me from acting for the client on this matter."
                    />
                    <Gutter bottom="24px" only="smallScreen" />
                  </GridColumn>
                  <GridColumn smallScreenWidth={12} largeScreenWidth={4}>
                    <WorkFeedEOIJobDescription workFeedItem={workFeedItem} />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                name="PENDO_CONFIRM_INTEREST_SELF_SERVICE_MODAL_CANCEL"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>

              <Button
                name="PENDO_CONFIRM_INTEREST_SELF_SERVICE_MODAL_SUBMIT"
                onClick={fireSubmit}
              >
                {submitButtonText}
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
